---
ID: 66d782184a9fb5d976f7d51a
---

<script>
    import Loader from "$lib/components/elements/Loader.svelte"

    const blocks = [
  {
    "id": "cpgpigW-En",
    "type": "text",
    "html": "<p>When it comes to conveying complex concepts in your documentation,&nbsp;<b>Mermaid graphs</b>&nbsp;are an essential tool.&nbsp;<b>EasyPageGO</b>&nbsp;makes it simple to create and edit these diagrams directly within the editor, helping you visualize your ideas effectively.</p><h2 id=\"directly-editable-in-the-editor\"><b>Directly Editable in the Editor</b></h2><p>One of the standout features of Mermaid graphs in&nbsp;<b>EasyPageGO</b>&nbsp;is the ability to&nbsp;<b>edit them directly within the editor</b>. This&nbsp;<b>real-time editing</b>&nbsp;capability allows you to make adjustments on the fly, ensuring that your diagrams are always current and accurately reflect your product's concepts.</p><h3 id=\"customizable-graph-sizing\">Customizable Graph Sizing</h3><p>While editing each graph in&nbsp;<b>EasyPageGO</b>, you have the flexibility to&nbsp;<b>adjust its size</b>&nbsp;directly within the editor. This allows you to determine the optimal dimensions for your diagrams, ensuring they fit seamlessly into your content. The final output automatically accounts for the space required by each graph, helping to achieve the&nbsp;<b>best possible score for Cumulative Layout Shift (</b><b><u><a href=\"https://web.dev/articles/cls\" target=\"_blank\" rel=\"noopener noreferrer\">CLS</a></u></b><b>)</b>. By reserving space for your graphs upfront,&nbsp;<b>EasyPageGO</b>&nbsp;ensures that your pages remain stable and visually consistent, enhancing both user experience and SEO performance.</p>"
  },
  {
    "type": "video",
    "id": "XT3oRzYqJw",
    "src": "https://easypagego.com/videos/add-image.mp4",
    "width": 600,
    "height": 400,
    "loop": true,
    "muted": true,
    "autoplay": true
  },
  {
    "id": "GVMVilT68P",
    "type": "text",
    "html": "<h2 id=\"19-different-diagrams\"><b>19 Different Diagrams</b></h2><p><b><u><a href=\"https://mermaid.js.org/\" target=\"_blank\" rel=\"noopener noreferrer\">Mermaid</a></u></b>&nbsp;supports a wide variety of diagram types, giving you the flexibility to choose the best representation for your information. With&nbsp;<b>19 different diagram options</b>, you can create everything from&nbsp;<b>flowcharts</b>&nbsp;and&nbsp;<b>sequence diagrams</b>&nbsp;to&nbsp;<b>Gantt charts</b>&nbsp;and&nbsp;<b>class diagrams</b>. This versatility helps you effectively communicate intricate ideas and processes in a visually compelling way.</p><h3 id=\"flowchart\">Flowchart</h3><p>A&nbsp;<b>flowchart</b>&nbsp;is a visual representation of a process or workflow, using symbols and arrows to illustrate the sequence of steps and decisions involved. It simplifies complex processes by breaking them down into manageable parts, making it easier to understand and communicate how a system operates.</p><p><b>Flowcharts</b>&nbsp;are particularly useful in&nbsp;<b>project management, software development</b>, and&nbsp;<b>process improvement</b>&nbsp;to&nbsp;<b>map out procedures, identify bottlenecks</b>, and&nbsp;<b>clarify roles and responsibilities</b>.</p>"
  },
  {
    "type": "mermaid",
    "id": "Yt0l8_Sjhs",
    "code": "```mermaid\nflowchart TD\n    A[Start] --> B{Is API Key Valid?}\n    B -->|Yes| C[Make API Request]\n    C --> F{Success?}\n    F --> |Yes| G[Display results]\n    F --> |No| H[Display error]\n    B -->|No| H\n```",
    "width": 230,
    "height": 543
  },
  {
    "id": "Fq-5Pmmd_K",
    "type": "callout",
    "variant": "note",
    "html": "<p>Read the&nbsp;<u><a href=\"https://mermaid.js.org/syntax/flowchart.html\" target=\"_blank\" rel=\"noopener noreferrer\">official docs</a></u>.</p>"
  },
  {
    "id": "vnGkxhoLf1",
    "type": "text",
    "html": "<h3 id=\"sequence\">Sequence</h3><p>A&nbsp;<b>sequence diagram</b>&nbsp;illustrates how objects or components interact in a specific sequence over time. It visually represents the&nbsp;<b>order of messages</b>&nbsp;exchanged between participants, showing the flow of control and data within a system.&nbsp;<b>Sequence diagrams</b>&nbsp;are especially useful for modeling dynamic behavior, such as&nbsp;<b>software design, API interactions</b>, and&nbsp;<b>user authentication processes</b>.</p><p>They help clarify the&nbsp;<b>sequence of events</b>&nbsp;and&nbsp;<b>interactions between components</b>, ensuring a shared understanding of complex processes.</p>"
  },
  {
    "type": "mermaid",
    "id": "6vKtkq5NiH",
    "code": "```mermaid\nsequenceDiagram\n    participant User\n    participant Client\n    participant AuthServer as Authorization Server\n    participant ResourceServer as Resource Server\n\n    User->>Client: Request Authentication\n    Client->>AuthServer: Request Token\n    AuthServer-->>Client: Return Token\n    Client->>ResourceServer: Request Resource with Token\n    ResourceServer-->>Client: Return Resource\n    Client-->>User: Provide Resource\n```",
    "width": 688,
    "height": 339
  },
  {
    "id": "EsjUPQOBo7",
    "type": "callout",
    "variant": "note",
    "html": "<p>Read the&nbsp;<u><a href=\"https://mermaid.js.org/syntax/sequenceDiagram.html\" target=\"_blank\" rel=\"noopener noreferrer\">official docs</a></u>.</p>"
  },
  {
    "id": "r0Zyvsghk5",
    "type": "text",
    "html": "<h3 id=\"state\">State</h3><p>A&nbsp;<b>state diagram</b>&nbsp;is a visual representation of the different states an object or system can be in, and how it transitions between these states in response to various events or conditions. Each state represents a particular condition or situation, and arrows (transitions) show how and when the system moves from one state to another.</p>"
  },
  {
    "type": "mermaid",
    "id": "K1eCScmBpx",
    "code": "```mermaid\nstateDiagram-v2\n    [*] --> Red\n    Red --> Green : Timer expires\n    Green --> Orange : Timer expires\n    Orange --> Red : Timer expires\n\n```",
    "width": 200,
    "height": 305
  },
  {
    "id": "AjdcpZcGPZ",
    "type": "callout",
    "variant": "note",
    "html": "<p>Read the&nbsp;<u><a href=\"https://mermaid.js.org/syntax/stateDiagram.html\" target=\"_blank\" rel=\"noopener noreferrer\">official docs</a></u>.</p>"
  },
  {
    "id": "km8C03zKIY",
    "type": "text",
    "html": "<h3 id=\"class\">Class</h3><p>A&nbsp;<b>class diagram</b>&nbsp;represents the classes, interfaces, and their relationships within a system. It illustrates the&nbsp;<b>attributes and methods</b>&nbsp;of each class, as well as the&nbsp;<b>associations, inheritances</b>, and&nbsp;<b>dependencies</b>&nbsp;between them.</p><p><b>Class diagrams</b>&nbsp;are essential in&nbsp;<b>object-oriented design and programming</b>, helping developers understand the system's architecture and how different components interact. They are particularly useful when&nbsp;<b>designing new systems, documenting existing code</b>, or&nbsp;<b>communicating the structure of software applications</b>&nbsp;to stakeholders.</p>"
  },
  {
    "type": "mermaid",
    "id": "GbW9Kv5b9U",
    "code": "```mermaid\nclassDiagram\n    class ApiClient {\n        +String baseUrl\n        +String apiKey\n        +connect()\n        +getRequest(endpoint: String)\n        +postRequest(endpoint: String, data: Object)\n        +handleResponse(response: Object)\n    }\n\n    class Auth {\n        +String token\n        +login(username: String, password: String)\n        +logout()\n    }\n\n    class Resource {\n        +String id\n        +String name\n        +fetch()\n    }\n\n    ApiClient --> Auth : uses\n    ApiClient --> Resource : manages\n```",
    "width": 407,
    "height": 385
  },
  {
    "id": "NpK5F9hwPe",
    "type": "callout",
    "variant": "note",
    "html": "<p>Read the&nbsp;<u><a href=\"https://mermaid.js.org/syntax/classDiagram.html\" target=\"_blank\" rel=\"noopener noreferrer\">official docs</a></u>.</p>"
  }
];
</script>

{#each blocks as block}
<Loader {block}/>
{/each}